import React from 'react';
import { Link } from 'react-router-dom';
import { differenceInCalendarDays, format, formatDistance } from 'date-fns';

import { Divider, Text, TextButton } from '@zillow/constellation-10';

import BillingConstants from '../constants/BillingConstants';
import BillingInvoicePage from '../pages/BillingInvoicePage';
import { numberUtils } from '@zg-rentals/rental-manager-env';
import { type AdproductsZuoraBillingInvoiceDto, ZuoraInvoiceType } from '@zg-rentals/ts-adproducts-client';

import PropertyNameList from '../components/PropertyNameList';
import { Box } from '@zillow/constellation-10';
import type { ColumnConfig } from '@zg-rentals/advanced-table';
import { EVENT_IDS } from '../model/ClickstreamEvents';
import { localDateToDate } from '../utils/dateUtils';

const PAST_DUE_DAYS = 30;

const renderDateWithPastDue = (invoice: AdproductsZuoraBillingInvoiceDto) => {
  if (!invoice.invoiceLocalDate) return undefined;
  const invoiceDate = localDateToDate(invoice.invoiceLocalDate);
  const dueBy = new Date(invoiceDate);
  dueBy.setDate(dueBy.getDate() + PAST_DUE_DAYS);
  const now = new Date();
  const isPastDue = differenceInCalendarDays(dueBy, now) < 0;
  return (
    <>
      <div>{format(invoiceDate, 'MM/dd/yy')}</div>
      {isPastDue && (
        <Text textStyle="body-xs" css={{ color: 'red.500' }}>
          {formatDistance(now, dueBy)} past due
        </Text>
      )}
    </>
  );
};

const invoiceRenderer = (invoice: AdproductsZuoraBillingInvoiceDto) =>
  invoice.invoiceType !== ZuoraInvoiceType.NUCLEUS_LISTING ? (
    <Link to={`/rental-manager/billing/invoice/${invoice.invoiceNumber}?prev=due`}>
      <TextButton
        data-eventid={EVENT_IDS.VIEW_RENTALS_BILLING_INVOICE}
        data-eventdescription="View billing invoice page event"
      >
        {invoice.invoiceNumber}
      </TextButton>
    </Link>
  ) : (
    <Box css={{ wordBreak: 'break-all' }}>{invoice.invoiceNumber}</Box>
  );

const fullColumnConfig: Array<ColumnConfig<AdproductsZuoraBillingInvoiceDto>> = [
  {
    width: 2,
    dataKey: 'invoiceLocalDate',
    header: 'Date',
    align: 'left',
    renderer: renderDateWithPastDue,
  },
  {
    width: 2,
    dataKey: 'invoiceNumber',
    header: 'Invoice No.',
    align: 'left',
    renderer: invoiceRenderer,
  },
  {
    width: 2,
    dataKey: 'propertyNames',
    header: 'Properties',
    align: 'left',
    renderer: (invoice: AdproductsZuoraBillingInvoiceDto) => <PropertyNameList invoice={invoice} />,
  },
  {
    width: 2,
    align: 'left',
    header: 'Account Number',
    dataKey: 'accountNumber',
  },
  {
    width: 2,
    dataKey: 'amount',
    header: 'Amount',
    renderer: (invoice: AdproductsZuoraBillingInvoiceDto) => numberUtils.asCurrency(invoice.amount),
    align: 'right',
  },
  {
    width: 2,
    dataKey: 'balance',
    header: 'Balance',
    renderer: (invoice: AdproductsZuoraBillingInvoiceDto) => numberUtils.asCurrency(invoice.balance),
    align: 'right',
  },
];

const mobileColumnConfig: Array<ColumnConfig<AdproductsZuoraBillingInvoiceDto>> = [
  {
    width: 3,
    dataKey: 'invoiceLocalDate',
    header: 'Date',
    align: 'left',
    renderer: renderDateWithPastDue,
  },
  {
    width: 4,
    dataKey: 'invoiceNumber',
    header: 'Invoice No.',
    align: 'left',
    renderer: (invoice: AdproductsZuoraBillingInvoiceDto) => (
      <>
        {invoiceRenderer(invoice)}
        {invoice.invoiceType === ZuoraInvoiceType.NUCLEUS_LISTING && <Divider />}
        <div>
          <PropertyNameList invoice={invoice} />
        </div>
        <div>{invoice.accountNumber}</div>
      </>
    ),
  },
  {
    width: 3,
    dataKey: 'balance',
    header: 'Balance',
    renderer: (invoice: AdproductsZuoraBillingInvoiceDto) => numberUtils.asCurrency(invoice.balance),
    align: 'right',
  },
];

const PaymentDuePage = () => (
  <BillingInvoicePage
    emptyMessage={BillingConstants.EMPTY_NOTIFICATIONS.NO_DUE_INVOICES}
    fullColumnConfig={fullColumnConfig}
    isPaymentDue={true}
    mobileColumnConfig={mobileColumnConfig}
    showHistory={false}
    title={'Payment Due'}
  />
);

export default PaymentDuePage;
